import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import ClientOAuth2 from 'client-oauth2';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.services';
import { take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';


declare const Meteor: any;

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  standalone: false
})
export class AuthCallbackComponent implements OnInit {
  isLoading = true;
  error: string | null = null;

  private googleAuth: ClientOAuth2;
  private facebookAuth: ClientOAuth2;
  private twitterAuth: ClientOAuth2;
  private linkedinAuth: ClientOAuth2;
  private amazonAuth: ClientOAuth2;
  private yahooAuth: ClientOAuth2;
  private appleAuth: ClientOAuth2;
  private vimeoAuth: ClientOAuth2;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {
    this.facebookAuth = new ClientOAuth2({
      clientId: environment.facebookClientId as string,
      accessTokenUri: 'https://graph.facebook.com/v7.0/oauth/access_token',
      authorizationUri: 'https://www.facebook.com/v7.0/dialog/oauth',
      redirectUri: `${window.location.origin}/auth/callback/facebook`,
      scopes: ['user_photos', 'email', 'user_gender', 'user_birthday', 'public_profile']
    });

    this.googleAuth = new ClientOAuth2({
      clientId: environment.googleClientId,
      accessTokenUri: 'https://accounts.google.com/o/oauth2/token',
      authorizationUri: 'https://accounts.google.com/o/oauth2/auth',
      redirectUri: `${window.location.origin}/auth/callback/google`,
      scopes: [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ],
    });

    this.linkedinAuth = new ClientOAuth2({
      clientId: environment.linkedinClientId,
      clientSecret: environment.linkedinClientSecret,
      accessTokenUri: 'https://www.linkedin.com/oauth/v2/accessToken',
      authorizationUri: 'https://www.linkedin.com/oauth/v2/authorization',
      redirectUri: `${window.location.origin}/auth/callback/linkedin`,
      scopes: ['openid', 'profile', 'email'],
    });

    this.twitterAuth = new ClientOAuth2({
      clientId: environment.twitterClientId,
      clientSecret: environment.twitterClientSecret,
      accessTokenUri: 'https://api.twitter.com/2/oauth2/token',
      authorizationUri: 'https://twitter.com/i/oauth2/authorize',
      redirectUri: `${window.location.origin}/auth/callback/x`,
      scopes: ['tweet.read', 'users.read', 'offline.access'],
    });

    this.appleAuth = new ClientOAuth2({
      clientId: environment.appleClientId,
      clientSecret: environment.appleClientSecret,
      accessTokenUri: 'https://appleid.apple.com/auth/token',
      authorizationUri: 'https://appleid.apple.com/auth/authorize',
      redirectUri: `${window.location.origin}/auth/callback/apple`,
      scopes: ['name', 'email'],
    });

    this.yahooAuth = new ClientOAuth2({
      clientId: environment.yahooClientId,
      clientSecret: environment.yahooClientSecret,
      accessTokenUri: 'https://api.login.yahoo.com/oauth2/token',
      authorizationUri: 'https://api.login.yahoo.com/oauth2/request_auth',
      redirectUri: `${window.location.origin}/auth/callback/yahoo`,
      scopes: ['email'],
    });

    this.amazonAuth = new ClientOAuth2({
      clientId: environment.amazonClientId,
      clientSecret: environment.amazonClientSecret,
      accessTokenUri: 'https://api.amazon.com/oauth2/token',
      authorizationUri: 'https://www.amazon.com/ap/oa',
      redirectUri: `${window.location.origin}/auth/callback/amazon`,
      scopes: ['openid', 'profile:name', 'profile:user_id', 'postal_code'],
      state: Date.now().toString()
    });

    this.vimeoAuth = new ClientOAuth2({
      clientId: environment.vimeoClientId,
      clientSecret: environment.vimeoClientSecret,
      accessTokenUri: 'https://api.vimeo.com/oauth/access_token',
      authorizationUri: 'https://api.vimeo.com/oauth/authorize',
      redirectUri: `${window.location.origin}/auth/callback/vimeo`,
      scopes: ['public', 'private', 'email'],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      // Get the current URL with query parameters
      const currentUrl = window.location.href;

      // grab the provider
      const provider = this.route.snapshot.params['provider'];

      // capture the code from the url
      let token: any;

      if (provider === 'google') {
        token = await this.googleAuth.token.getToken(currentUrl);
      } else if (provider === 'facebook') {
        token = await this.facebookAuth.token.getToken(currentUrl);
      } else if (provider === 'twitter' || provider === 'twitter') {
        token = await this.twitterAuth.token.getToken(currentUrl);
      } else if (provider === 'vimeo') {
        token = await this.vimeoAuth.token.getToken(currentUrl);
      } else if (provider === 'apple') {
        token = await this.appleAuth.token.getToken(currentUrl);
      } else if (['linkedin', 'yahoo', 'amazon', 'vimeo'].includes(provider)) {
        const code = this.route.snapshot.queryParams['code'];
        const callbackUrl = `${window.location.origin}/auth/callback/${provider}`;
        token = await this.authService.getTokenFromCode(code, callbackUrl).pipe(take(2)).toPromise();
      } else {
        this.error = `Unsupported provider: ${provider}`;
        this.isLoading = false;
        return this.showError();
      }

      if (token?.accessToken || token?.access_token || token?.data?.access_token || token?.data?.accessToken) {
        sessionStorage.setItem('token', JSON.stringify(token?.accessToken || token?.access_token || token?.data?.access_token || token?.data?.accessToken));
        this.authenticateUser(provider, token?.accessToken || token?.access_token || token?.data?.access_token || token?.data?.accessToken);
        this.isLoading = true;
      } else {
        this.error = 'Authentication failed';
        this.isLoading = false;
        return this.showError();
      }
    } catch (error: any) {
      this.error = error.message || 'Authentication failed';
      this.isLoading = false;
      this.showError();
    }
  }

  private authenticateUser(provider: string, token: string): void {
    this.isLoading = true;

    Meteor.call('getConsumerProfile', provider, token, (error: any, response: any) => {
      this.isLoading = false;
      console.info("response:==>>", response);
      if (error) {
        this.error = error.message || 'Failed to authenticate user';
        this.showError();
        return;
      }

      if (response && response.code === 200) {
        // Store the user profile and refresh auth state so the app has user data
        sessionStorage.setItem('tokenPayload', JSON.stringify(response.data));
        this.authService.refreshUser();
        this.router.navigate(['/home']);
      } else {
        this.error = response?.message || 'Authentication failed';
        this.showError();
      }
    });
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  private showError(): void {
    Swal.fire({
      icon: 'error',
      title: 'Authentication Error',
      text: this.error || 'An error occurred during authentication. Please try again.',
      confirmButtonText: 'Go to Login',
      background: '#1a1a2e',
      color: '#fff',
    }).then(() => {
      this.router.navigate(['/login']);
    });
  }
}
